import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function PaymentCollections() {
  const { payments, loading } = useWebData(); 

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!payments) return;

    let result = [...payments];

    // Search Logic
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.date ? new Date(i.date).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            
            const row = `${dateStr} ${i.orgName} ${i.amount} ${i.userName} ${i.mode} ${i.bankName} ${i.refNumber} ${i.billRef}`.toLowerCase();
            return row.includes(lower);
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.date || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    setFiltered(result);
  }, [payments, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.date),
        Collected_By: i.userName,
        Party_Name: i.orgName,
        Address: i.address || '-',
        City: i.city || '-',
        Contact: i.contactPerson || '-',
        Amount_Received: i.amount,
        Balance_Pending: i.pendingAmount || 0,
        Mode: i.mode,
        Bank_Name: i.bankName || '-',
        Ref_Cheque_No: i.refNumber || '-',
        PDC_Date: i.pdcDate ? formatDate(i.pdcDate) : '-',
        Bill_Ref: i.billRef || '-',
        Notes: i.notes
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Collections");
    XLSX.writeFile(wb, `Payment_Collections_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const handleDelete = async (id) => {
      if(window.confirm("Delete this receipt? This cannot be undone.")) {
          await deleteDoc(doc(db, "payment_collections", id));
      }
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  const totalCollected = filtered.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <div>
                <h2 style={{margin:0, color:"#27ae60", display:"flex", alignItems:"center"}}>
                    💰 Collections <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
                </h2>
                <span style={{fontSize:"13px", fontWeight:"bold", color:"#333", marginTop:"5px", display:"block"}}>
                    Total: ₹{totalCollected.toLocaleString()}
                </span>
            </div>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Party, Amount..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#27ae60", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Party Name</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Mode</th>
                    <th style={thStyle}>Details</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No payments found.</td></tr>
              ) : filtered.map(i => (
                <tr key={i.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(i.date)}</td>
                    <td style={{...tdStyle, fontWeight:"bold"}}>{i.userName}</td>
                    <td style={{...tdStyle, textAlign:"left"}}>{i.orgName}</td>
                    <td style={{...tdStyle, color:"green", fontWeight:"bold", fontSize:"13px"}}>₹{i.amount}</td>
                    <td style={tdStyle}>{i.mode}</td>
                    <td style={{...tdStyle, fontSize:"11px", color:"gray"}}>
                        {i.mode !== 'Cash' ? `${i.bankName || ''} ${i.refNumber || ''}` : '-'}
                        {i.billRef && <div style={{color:"black"}}>Ref: {i.billRef}</div>}
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            <button onClick={() => setViewData(i)} style={btnIcon} title="View Details">👁️</button>
                            <button onClick={() => handleDelete(i.id)} style={btnIconDelete} title="Delete">🗑️</button>
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#27ae60"}}>🧾 Receipt Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", fontSize:"13px", lineHeight:"1.6"}}>
                <div>
                    <p><strong>📅 Date:</strong> {formatDate(viewData.date)}</p>
                    <p><strong>👤 Collected By:</strong> {viewData.userName}</p>
                    <p><strong>🏢 Party:</strong> {viewData.orgName}</p>
                    <p><strong>📍 City:</strong> {viewData.city || '-'}</p>
                </div>
                <div>
                    <p><strong>💵 Amount:</strong> ₹{viewData.amount}</p>
                    <p><strong>💳 Mode:</strong> {viewData.mode}</p>
                    <p><strong>🏦 Bank:</strong> {viewData.bankName || '-'}</p>
                    <p><strong>🔢 Ref/Cheque:</strong> {viewData.refNumber || '-'}</p>
                </div>
            </div>
            
            {viewData.pdcDate && (
                <div style={{background:"#ffebee", padding:"8px", marginTop:"10px", borderRadius:"5px", color:"red", fontWeight:"bold"}}>
                    ⚠️ PDC Date: {formatDate(viewData.pdcDate)}
                </div>
            )}
            
            <hr style={{border:"0.5px solid #eee", margin:"10px 0"}}/>
            <p style={{fontSize:"13px"}}><strong>🧾 Bill Ref:</strong> {viewData.billRef || '-'}</p>
            <p style={{fontSize:"13px"}}><strong>📉 Balance Pending:</strong> ₹{viewData.pendingAmount || 0}</p>
            
            <div style={{background:"#f9f9f9", padding:"10px", marginTop:"10px", borderRadius:"5px"}}>
                <strong style={{fontSize:"12px", color:"#555"}}>📝 Notes:</strong>
                <p style={{margin:"5px 0 0 0", fontSize:"13px"}}>{viewData.notes || 'No notes'}</p>
            </div>

            <button onClick={()=>setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#2980b9", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"450px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default PaymentCollections;